import {GitHubAvatar} from '@github-ui/github-avatar'
import {IssueStateIcons, PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {Link as RouterLink} from '@github-ui/react-router'
import {MilestoneIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, AvatarStack, Button, Link as PrimerLink, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useId, useRef, useState} from 'react'

import type {BoardPayloadAssignee, BoardPayloadItem, BoardPayloadLabel} from './board-payload'
import styles from './BoardCard.module.css'

/**
 * Icons come from `@github-ui/issue-pr-state`, so every surface that shows issue/PR state
 * agrees on the glyph, colour, and accessible name.
 */
function StateIcon({item}: {item: BoardPayloadItem}) {
  if (item.type === 'issue') {
    if (item.state === 'open') return <IssueStateIcons.OPEN size={12} />
    if (item.stateReason === 'not_planned') return <IssueStateIcons.NOT_PLANNED size={12} />
    if (item.stateReason === 'duplicate') return <IssueStateIcons.DUPLICATE size={12} />
    return <IssueStateIcons.COMPLETED size={12} />
  }

  switch (item.state) {
    case 'draft':
      return <PullRequestStateIcons.DRAFT size={12} />
    case 'merged':
      return <PullRequestStateIcons.MERGED size={12} />
    case 'closed':
      return <PullRequestStateIcons.CLOSED size={12} />
    case 'queued':
      return <PullRequestStateIcons.QUEUED size={12} />
    case 'archived':
      return <PullRequestStateIcons.ARCHIVED size={12} />
    default:
      return <PullRequestStateIcons.OPEN size={12} />
  }
}

/**
 * One card, drawn from a payload item.
 */
export function BoardCard({item}: {item: BoardPayloadItem}) {
  const labels = item.fields.label?.value ?? []
  const assignees = item.fields.assignee?.value ?? []
  const milestone = item.fields.milestone?.value ?? null
  const itemUrl = `/${item.repoNameWithOwner}/${item.type === 'issue' ? 'issues' : 'pull'}/${item.number}`

  return (
    <div className={styles.card}>
      <div className={styles.reference}>
        <span className={styles.referenceText}>
          <StateIcon item={item} />
          {`${item.repoNameWithOwner} #${item.number}`}
        </span>
        <Assignees assignees={assignees} />
      </div>

      <PrimerLink as={RouterLink} to={itemUrl} className={styles.title}>
        {item.title}
      </PrimerLink>

      {labels.length === 0 ? null : <Labels labels={labels} repoNameWithOwner={item.repoNameWithOwner} />}

      {milestone === null ? null : (
        <div className={styles.meta}>
          <PrimerLink href={milestone.url} className={styles.milestone}>
            <MilestoneIcon size={12} aria-label="Milestone" />
            <span>{milestone.title}</span>
          </PrimerLink>
        </div>
      )}
    </div>
  )
}

/**
 * Each avatar links to its assignee with a tooltip that shows the login.
 */
function Assignees({assignees}: {assignees: readonly BoardPayloadAssignee[]}) {
  if (assignees.length === 0) return null

  return (
    <span className={styles.assignees}>
      {/* 24px avatars so each target meets WCAG 2.5.8 minimum. The "equivalent control"
          exception doesn't apply on the board view — no other 24×24 control on the page
          links to the assignee's profile. */}
      <AvatarStack alignRight size={24}>
        {assignees.map(assignee => (
          <AssigneeLink key={assignee.login} assignee={assignee} />
        ))}
      </AvatarStack>
    </span>
  )
}

function AssigneeLink({assignee, className}: {assignee: BoardPayloadAssignee; className?: string}) {
  return (
    <span className={clsx(className, styles.assigneeItem)}>
      <Tooltip text={assignee.login} type="label" direction="n">
        <PrimerLink href={`/${assignee.login}`}>
          <GitHubAvatar alt={assignee.login} src={assignee.avatarUrl} size={24} />
        </PrimerLink>
      </Tooltip>
    </span>
  )
}

/**
 * How many labels a card names before the rest roll into the overflow.
 *
 * Three, where the issues index caps nothing and lets every label it fetched run inline. A
 * card is a fifth of a row's width, so a row's answer would be several lines of tokens on
 * one; the count is what a card has instead of the room.
 */
const MAX_INLINE_LABELS = 3

/**
 * How many of the collapsed names the count carries in its tooltip before it stops naming
 * them and counts the remainder. An issue can hold dozens of labels, and a description that
 * grows with every one of them is read out in full on focus.
 */
const MAX_NAMED_OVERFLOW_LABELS = 3

/**
 * The card's labels: the first few as their own tokens, then a count standing for the rest.
 *
 * The count opens a list of the labels it stands for, each a link to that label's issues.
 * A few of their names are on the count as well, so hovering reads them without opening
 * anything.
 */
function Labels({labels, repoNameWithOwner}: {labels: readonly BoardPayloadLabel[]; repoNameWithOwner: string}) {
  const inline = labels.slice(0, MAX_INLINE_LABELS)
  const overflow = labels.slice(MAX_INLINE_LABELS)

  return (
    <div className={styles.labels}>
      {inline.map(label => (
        <Label key={label.id} label={label} repoNameWithOwner={repoNameWithOwner} />
      ))}
      {overflow.length === 0 ? null : <LabelOverflow labels={overflow} repoNameWithOwner={repoNameWithOwner} />}
    </div>
  )
}

/**
 * The labels a card had no room to name, as a count that opens them.
 *
 * A disclosure over a plain list of links rather than an `ActionMenu`: every entry only
 * navigates to a label's issues, and `role="menu"`/`role="menuitem"` must not stand for
 * navigation (`.github/agents/resources/accessibility/global-prohibitions.md`). Opening on
 * a click rather than on the hover: a list holding links has to be somewhere a pointer can
 * travel into and a keyboard can reach, and Primer's tooltip does not open that way. Only
 * the labels behind the count, since the ones in front of it are already named and already
 * linked.
 */
function LabelOverflow({labels, repoNameWithOwner}: {labels: readonly BoardPayloadLabel[]; repoNameWithOwner: string}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLElement>(null)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  return (
    <AnchoredOverlay
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      align="start"
      anchorRef={anchorRef}
      renderAnchor={anchorProps => (
        // Primer's tooltip owns the trigger ref, so the overlay is anchored through it.
        <Tooltip ref={anchorRef} direction="n" text={overflowSummary(labels)}>
          <Button
            {...anchorProps}
            // Every `aria-haspopup` value names a widget this overlay is not; `aria-expanded` alone
            // is what a disclosure reports.
            aria-haspopup={undefined}
            variant="invisible"
            size="small"
            className={styles.labelOverflowButton}
          >
            {`+${labels.length}`}
            <span className="sr-only">{labels.length === 1 ? ' more label' : ' more labels'}</span>
          </Button>
        </Tooltip>
      )}
    >
      <ActionList aria-label="More labels" className={styles.labelOverflowList}>
        {labels.map(label => (
          <ActionList.LinkItem key={label.id} href={labelHref(label, repoNameWithOwner)}>
            <RepositoryLabelToken label={toTokenLabel(label)} />
          </ActionList.LinkItem>
        ))}
      </ActionList>
    </AnchoredOverlay>
  )
}

/**
 * The names the count carries in its tooltip: the first few, then how many are left.
 */
function overflowSummary(labels: readonly BoardPayloadLabel[]): string {
  const named = labels.slice(0, MAX_NAMED_OVERFLOW_LABELS).map(label => label.name)
  const remaining = labels.length - named.length
  return remaining === 0 ? named.join(', ') : `${named.join(', ')} and ${remaining} more`
}

/**
 * Linked label token with a description tooltip. The description also lives in a hidden
 * span so screen readers reach it whether or not the tooltip is shown; the tooltip itself
 * is suppressed for labels that carry no description.
 */
function Label({label, repoNameWithOwner}: {label: BoardPayloadLabel; repoNameWithOwner: string}) {
  const tooltipId = useId()
  const description = label.description ?? ''
  return (
    <Tooltip
      text={description}
      type="description"
      className={clsx(styles.labelTooltip, !label.description && styles.tooltipHidden)}
    >
      <PrimerLink href={labelHref(label, repoNameWithOwner)} aria-describedby={tooltipId} className={styles.labelLink}>
        <RepositoryLabelToken label={toTokenLabel(label)} interactive />
        <span className="sr-only" id={tooltipId}>
          {description}
        </span>
      </PrimerLink>
    </Tooltip>
  )
}

function labelHref(label: BoardPayloadLabel, repoNameWithOwner: string): string {
  return `/${repoNameWithOwner}/labels/${encodeURIComponent(label.name)}`
}

/**
 * `nameHTML` is what carries emoji markup; the payload spells it `nameHtml` and the shared
 * token expects `nameHTML`, so it is renamed here until the payload aligns.
 */
function toTokenLabel(label: BoardPayloadLabel) {
  return {name: label.name, nameHTML: label.nameHtml, color: label.color}
}
